import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Can from 'components/Can';
import Feature from 'components/Feature';

import {
  Box,
  Card,
  Button,
  IconButton,
  CardHeader,
  CardActions,
  CardContent,
  LinearProgress,
} from '@mui/material';
import i18n from 'i18n-js';
import { VerticalMenu } from 'components/UI';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDispatch, useSelector } from 'react-redux';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import { UserSettingsActions } from 'state/modules/userSettings';

const CollapsibleButton = ({ handleCollapse, collapsed }) => {
  return (
    <IconButton
      data-cy="menu-btn"
      variant="contained"
      aria-haspopup="true"
      onClick={handleCollapse}
      aria-controls="collapsed-menu"
    >
      <ArrowDropUp
        sx={{
          fontSize: 32,
          transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      />
    </IconButton>
  );
};

CollapsibleButton.propTypes = {
  handleCollapse: PropTypes.func,
  collapsed: PropTypes.bool,
};

const BaseCard = ({
  id = `card-${Math.random().toString(36).slice(2)}`,
  title,
  onNext,
  datacy,
  onEnter,
  children,
  onCancel,
  subtitle,
  onRefresh,
  subheader,
  hideHeader,
  hasEnterButton,
  menuItems = [],
  hasCancelButton,
  menuPermissions,
  loading = false,
  headerActionType,
  actionButtonLabel,
  contentRootClasses = {},
  enableCollapse = false,
  cardProps,
  ...props
}) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const { collapsed } = useSelector((state) => state.userSettings);
  const [isCollapsed, setIsCollapsed] = useState(collapsed?.[id] || false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const displayVerticalMenu = () => {
    return (
      menuItems.length > 0 &&
      menuItems?.filter((el) => Object.prototype.hasOwnProperty.call(el, 'display') && !el?.display)
        ?.length !== menuItems.length
    );
  };

  const handleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    dispatch(UserSettingsActions.handleCollapseCard({ id, collapsed: !isCollapsed }));
  };

  const renderHeaderAction = () => {
    let menu;
    switch (headerActionType) {
      case 'vertical':
        if (menuItems.length > 0 && displayVerticalMenu()) {
          menu = (
            <IconButton
              variant="contained"
              aria-haspopup="true"
              onClick={handleClick}
              data-cy="vertical-menu-btn"
              aria-controls="customized-menu"
            >
              <MoreVertIcon />
            </IconButton>
          );
        }
        break;

      case 'button':
        menu = (
          <Button
            color="primary"
            onClick={onNext}
            variant="contained"
            sx={{
              marginTop: 8,
              marginRight: 18,
            }}
          >
            {actionButtonLabel}
          </Button>
        );
        break;
      default:
        return null;
    }

    return (
      <Box>
        {onRefresh && !isCollapsed && (
          <Button
            color="primary"
            onClick={onRefresh}
            aria-haspopup="true"
            data-cy="refresh-btn"
            aria-controls="customized-menu"
            sx={{
              fontSize: '14px',
              fontWeight: 700,
              color: 'primary.main',
              textTransform: 'Capitalize',
            }}
          >
            {i18n.t('common.refresh')}
          </Button>
        )}
        {enableCollapse && (
          <CollapsibleButton collapsed={isCollapsed} handleCollapse={handleCollapse} />
        )}
        {menu}
      </Box>
    );
  };

  const renderVerticalMenu = () => {
    if (!menuItems || !menuItems.length) return null;
    return <VerticalMenu items={menuItems} anchorEl={anchorEl} handleClose={handleClose} />;
  };

  return (
    <Card
      data-cy={datacy}
      sx={{ maxWidth: '100%', boxShadow: '0px 3px 6px #00000014' }}
      {...cardProps}
    >
      {!hideHeader && (
        <CardHeader
          title={title}
          subheader={subtitle}
          action={renderHeaderAction()}
          slotProps={{
            title: {
              variant: 'h2',
              component: 'h2',
            },
          }}
          sx={{
            display: 'flex',
            minHeight: '48px',
            alignItems: 'center',
            padding: '0px 20px 0px 32px',
            justifyContent: 'space-between',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,

            '& .MuiCardHeader-content': {
              '& .MuiCardHeader-title': {
                color: 'text.primary',
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'normal',
              },
            },

            '& .MuiCardHeader-action': {
              marginTop: 0,
              alignSelf: 'center',
            },
          }}
          {...props}
        />
      )}
      {headerActionType === 'vertical' && displayVerticalMenu() && (
        <>
          {menuPermissions?.type === 'can' ? (
            <Can I={menuPermissions.action} on={menuPermissions.subject}>
              {renderVerticalMenu()}
            </Can>
          ) : menuPermissions?.type === 'feature' ? (
            <Feature name={menuPermissions?.action} is={menuPermissions?.subject}>
              {renderVerticalMenu()}
            </Feature>
          ) : (
            <>{renderVerticalMenu()}</>
          )}
        </>
      )}

      {subheader && !collapsed?.[id] && (
        <Box sx={{ backgroundColor: '#F9FCFF', padding: '24px 32px' }}>{subheader}</Box>
      )}

      {collapsed?.[id] ? null : loading ? (
        <LinearProgress />
      ) : (
        <Box sx={{ height: '4px', overflow: 'hidden', position: 'relative' }} />
      )}

      <CardContent
        sx={{
          transition: 'all 0.2s linear',
          opacity: collapsed?.[id] ? 0 : 1,
          maxHeight: collapsed?.[id] ? 0 : 10000,
          padding: collapsed?.[id] ? 0 : '24px 32px',
          ...contentRootClasses,
        }}
      >
        {children}
      </CardContent>

      {(hasCancelButton || hasEnterButton) && (
        <CardActions
          sx={{
            backgroundColor: '#F6F6F6',
            padding: '16px 32px',
            justifyContent: `${hasCancelButton && hasEnterButton ? 'space-around' : 'flex-end'}`,
          }}
        >
          {hasCancelButton && (
            <Button variant="outlined" onClick={onCancel} color="primary">
              Cancel
            </Button>
          )}
          {hasEnterButton && (
            <Button variant="contained" onClick={onEnter} color="primary">
              Continue
            </Button>
          )}
        </CardActions>
      )}
    </Card>
  );
};

BaseCard.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  datacy: PropTypes.string,
  onNext: PropTypes.func,
  onEnter: PropTypes.func,
  onCancel: PropTypes.func,
  onRefresh: PropTypes.func,
  menuItems: PropTypes.array,
  enableCollapse: PropTypes.bool,
  menuPermissions: PropTypes.object,
  headerActionType: PropTypes.string,
  actionButtonLabel: PropTypes.string,
  contentRootClasses: PropTypes.object,
  loading: PropTypes.bool,
  children: PropTypes.node,
  subtitle: PropTypes.string,
  hasEnterButton: PropTypes.bool,
  hasCancelButton: PropTypes.bool,
  subheader: PropTypes.node,
  hideHeader: PropTypes.bool,
  cardProps: PropTypes.object,
};

export default BaseCard;
